import { computePriceEstimateGrandTotalOrNull, formatPriceLine, formatTrancheLine } from "./pricingCalculations";
import { formatLongDateIst } from "./formatDdMmYyyy";
import {
  buildContractParties,
  buildScopeOfWork,
  buildLocationSummary,
  buildDurationLabel,
} from "./contractParties";

/**
 * Builds MSA exhibit section strings from persisted project profile (scoping_profile_json),
 * matching ProjectWorkspace `computeContractSections` semantics so onboarding can show a
 * live contract preview without a saved `scopingContractDraft`.
 */
export function buildContractPreviewFromProfile(project, firm = null) {
  const profile = project?.profile || {};
  const attrs = profile.attributes || [];
  const getAttr = (k) => (attrs.find((a) => a.key === k) || { value: "" }).value || "";

  let locationDetails = [];
  try {
    locationDetails = JSON.parse(getAttr("locationDetails") || "[]");
  } catch {
    locationDetails = [];
  }
  if (!Array.isArray(locationDetails)) locationDetails = [];

  const locSummary = buildLocationSummary(locationDetails);

  const milestoneItems = Array.isArray(profile.milestones) ? profile.milestones : [];
  const raciRows = Array.isArray(profile.raci) ? profile.raci : [];
  const defaultResourceHeader = [
    "Phase ID",
    "Phase title",
    "Task title",
    "Lead Advisor",
    "Advisor",
    "Associate Advisor",
    "Technical Advisor",
  ];
  const resourceTable =
    profile.resources && typeof profile.resources === "object" && (profile.resources.rows || profile.resources.header)
      ? profile.resources
      : { header: defaultResourceHeader, rows: [] };

  const priceEstimate = Array.isArray(profile.priceEstimate) ? profile.priceEstimate : [];
  const paymentTranches = Array.isArray(profile.paymentTranches) ? profile.paymentTranches : [];

  const milestoneSummary = milestoneItems
    .map((m) => `${m.title || "-"}: ${m.start || "-"} - ${m.due || "-"} (${m.duration ?? "-"} days)`)
    .join("\n") || "See milestone map";

  const raciSummaryText =
    raciRows
      .slice(0, 5)
      .map((r) => (Array.isArray(r) ? r.slice(1, 4).join(" | ") : String(r)))
      .join("\n") || "See RACI matrix";

  const effortLines = (resourceTable.rows || [])
    .slice(0, 8)
    .map((r) => `${r[1] || ""} - ${r[2] || ""}: ${r.slice(3).filter(Boolean).join(", ")} man-days`)
    .join("\n") || "See resource requirements";

  // null (some line unpriced / no lines) => blank, never "0".
  const totalPrice = computePriceEstimateGrandTotalOrNull(priceEstimate);

  const priceSummaryLines = priceEstimate
    .map((pe) => {
      if (pe.children && pe.children.length) {
        return (
          `${pe.expenseHead}:\n` +
          pe.children
            .map((c) => formatPriceLine(c, "  "))
            .join("\n")
        );
      }
      return formatPriceLine(pe);
    })
    .join("\n");

  const paymentLines = paymentTranches
    .map((t) => formatTrancheLine(t))
    .join("\n");

  return {
    effectiveDate: formatLongDateIst(Date.now()),
    ...buildContractParties({ getAttr, project, firm }),
    scopeOfWork: buildScopeOfWork({ project, getAttr, locationDetails, locSummary }),
    projectPlan: milestoneSummary,
    raciSummary: raciSummaryText,
    effortEstimate: effortLines,
    priceEstimateSummary: priceSummaryLines,
    paymentTerms: paymentLines,
    totalPayment: totalPrice === null ? "" : totalPrice.toLocaleString(),
    duration: buildDurationLabel(milestoneItems),
  };
}
